'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'

/**
 * Runs on every client-side route change.
 *
 * Next.js leaves keyboard focus wherever the clicked link was (or nowhere) and
 * only announces the new title through its live region. Research with screen
 * reader, magnification, voice and switch users (Fable Tech Labs, 2019) found
 * the best landing spot after a route change is the skip link itself: a small
 * control that becomes visible on keyboard use and lets the user bypass the
 * header again, exactly as on a fresh page load.
 *
 * - Keyboard user (has pressed Tab or an arrow key since their last pointer
 *   press): the skip link takes focus and is visible.
 * - Anyone else: the tab position is reset to just before the link, so the
 *   next Tab reaches it, and nothing becomes visible. This is tracked here
 *   rather than left to :focus-visible, whose heuristic treats any recent key
 *   press (typing, Enter, Escape) as keyboard use.
 *
 * The very first load is left alone: the browser already starts at the top.
 */
// Keys that mean the visitor is moving through the page with the keyboard.
// Typing in a field, Enter on a focused control and Escape are not that: the
// browser's own :focus-visible heuristic counts any recent key press, which
// made the skip link pop into view after, e.g., pressing Enter in a form.
const NAVIGATION_KEYS = new Set(['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'])

const ScrollToTop = () => {
    const pathname = usePathname()
    const isFirstLoad = useRef(true)
    // true once the visitor has pressed a navigation key since their last
    // pointer interaction; cleared by any mouse/touch/pen press
    const keyboardNavigation = useRef(false)

    useEffect(() => {
        const onKeyDown = (event) => {
            if (NAVIGATION_KEYS.has(event.key)) keyboardNavigation.current = true
        }
        const onPointerDown = () => { keyboardNavigation.current = false }
        window.addEventListener('keydown', onKeyDown, true)
        window.addEventListener('pointerdown', onPointerDown, true)
        return () => {
            window.removeEventListener('keydown', onKeyDown, true)
            window.removeEventListener('pointerdown', onPointerDown, true)
        }
    }, [])

    useEffect(() => {
        window.scrollTo(0, 0)

        if (isFirstLoad.current) {
            isFirstLoad.current = false
            return
        }

        const skipLink = document.querySelector('.skip-link')
        if ( ! skipLink) return

        if (keyboardNavigation.current) {
            // Keyboard user: the skip link is the landing spot and it shows
            skipLink.focus({ preventScroll: true })
            return
        }

        // Anyone else: park the tab position just before the link, so the
        // next Tab reaches it, without showing it
        const anchor = document.getElementById('page-top')
        if (anchor) {
            anchor.focus({ preventScroll: true })
            anchor.blur()
        }
    }, [pathname])

    return null
}

export default ScrollToTop
